import * as React from "react"
import Box from "@mui/material/Box"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import Select from "@mui/material/Select"
import {ReactComponent as ChevronDown} from "src/v2/assets/icons/chevron-down-icon.svg"
import PropTypes from "prop-types"
import {styled} from "@mui/material/styles"
import InputBase from "@mui/material/InputBase"
import InputAdornment from "@mui/material/InputAdornment"
import {ReactComponent as CloseIcon} from "src/v2/assets/icons/close-icon.svg"
import {IconButton} from "@mui/material"

const StyledInput = styled(InputBase, {
    shouldForwardProp: (prop) => prop !== "hasStartIcon" && prop !== "hasLabel" && prop !== "notched",
})(({theme, hasStartIcon, hasLabel}) => ({
    position: "relative",

    "& .MuiInputBase-input": {
        borderRadius: 0,
        border: `1px solid ${theme.palette.input.borderColor.primary}`,
        backgroundColor: theme.palette.input.background.white05,
        color: theme.palette.primary.white70,
        fontSize: theme.typography.fontSizes?.sm || "14px",
        height: "50px",
        padding: hasLabel
            ? "20px 12px 0 10px"
            : "12px 12px 12px 12px",
        boxSizing: "border-box",
        ...(!hasLabel && {
            display: "flex",
            alignItems: "center",
        }),

        ...(hasStartIcon && {
            paddingLeft: "40px",
        }),

        "&:hover": {
            color: theme.palette.primary.white70,
            backgroundColor: theme.palette.input.background.white30,
        },
        "&:focus": {
            color: theme.palette.primary.white70,
            backgroundColor: theme.palette.input.background.white30,
            borderRadius: 0,
        },
    },

    "& .MuiSelect-icon": {
        color: theme.palette.icon.main,
    },

    "& .MuiInputAdornment-root": {
        position: "absolute",
        left: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        color: theme.palette.icon.main,
    },
}))

const StyledLabel = styled(InputLabel, {
    shouldForwardProp: (prop) => prop !== "hasStartIcon",
})(({theme, hasStartIcon}) => ({
    color: theme.palette.primary.white70,
    left: hasStartIcon ? "40px" : "12px",
    top: 0,
    fontSize: theme.typography.fontSizes?.sm || "14px",
    transform: "none",
    lineHeight: 1.4,
    display: "flex",
    alignItems: "center",
    height: "50px",

    "&.MuiInputLabel-shrink": {
        top: "6px",
        height: "auto",
        alignItems: "flex-start",
        fontSize: "11px",
        transform: "none",
        color: theme.palette.primary.white70,
    },
    "&.Mui-focused": {
        color: theme.palette.primary.white70,
    },
}))

const normalizeOption = (option) => {
    if (option && typeof option === "object") {
        const val = option.value ?? option.label ?? ""
        const label = option.label ?? option.value ?? ""
        return {value: val, label}
    }
    return {value: option, label: option}
}

const SelectComponent = (
    {
        label,
        value,
        startIcon,
        onChange,
        options,
        width = "100%",
        error,
        helperText,
        clearable = false,
        clearAriaLabel = "Limpar seleção",
        onClear,
        shouldShrinkLabel: shouldShrinkLabelProp,
        ...props
    }) => {
    const [focused, setFocused] = React.useState(false)

    const normalizedOptions = React.useMemo(() => options.map(normalizeOption), [options])

    const shouldShrinkLabel = shouldShrinkLabelProp ??
        ((value !== "" && value !== null && value !== undefined) || focused)

    return (
        <Box sx={{width}}>
            <FormControl fullWidth>
                {label === null || label === undefined || label === "" ? null :
                    <StyledLabel
                        hasStartIcon={!!startIcon}
                        id={`${label}-select-label`}
                        shrink={shouldShrinkLabel}
                        className={startIcon ? "has-start-icon" : ""}
                    >
                        {label}
                    </StyledLabel>
                }
                <Select
                    labelId={`${label}-select-label`}
                    id={`${label}-select`}
                    value={value}
                    label={label}
                    onChange={onChange}
                    onOpen={() => setFocused(true)}
                    onClose={() => setFocused(false)}
                    IconComponent={ChevronDown}
                    sx={{
                        width: "100%",
                        minWidth: width,
                        "& .MuiSelect-select": {
                            paddingRight: clearable && value ? "60px !important" : "40px !important",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        },
                        "& .MuiSelect-icon": {
                            right: "12px",
                        },
                    }}
                    input={
                        <StyledInput
                            hasStartIcon={!!startIcon}
                            hasLabel={!!label}
                            startAdornment={
                                startIcon ? (
                                    <InputAdornment position="start">
                                        {React.cloneElement(startIcon, {
                                            style: {width: 24, height: 24},
                                        })}
                                    </InputAdornment>
                                ) : null
                            }
                        />
                    }
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 200,
                                "&::-webkit-scrollbar": {
                                    width: "16px",
                                    background: "transparent",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "#D9D9D9",
                                    opacity: "40%",
                                    borderRadius: "8px",
                                    border: `5px solid transparent`,
                                    backgroundClip: "padding-box",
                                    marginBlockStart: "16px",
                                },
                                "&::-webkit-scrollbar-track": {
                                    background: "transparent",
                                },
                            },
                        },
                    }}
                    displayEmpty
                    error={error}
                    {...props}
                >
                    {normalizedOptions.map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                                color: (theme) => theme.palette.common.white,
                                "&:hover": {
                                    backgroundColor: (theme) =>
                                        theme.palette.input.background.white30,
                                    color: (theme) =>
                                        theme.palette.primary.contrastText,
                                },
                                "&.Mui-selected": {
                                    backgroundColor: (theme) =>
                                        theme.palette.primary.dark,
                                    color: (theme) =>
                                        theme.palette.primary.contrastText,
                                },
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
                {helperText && (
                    <Box sx={{mt: 0.5, color: error ? "error.main" : "text.secondary", fontSize: "12px"}}>
                        {helperText}
                    </Box>
                )}

                {clearable && value !== "" && value !== null && value !== undefined && (
                    <IconButton
                        aria-label={clearAriaLabel}
                        onClick={(event) => {
                            event.stopPropagation()
                            onClear?.()
                        }}
                        sx={{
                            position: "absolute",
                            right: 28,
                            top: "28px",
                            transform: "translateY(-50%)",
                            zIndex: 2,
                        }}
                    >
                        <CloseIcon style={{width: 18, height: 18}}/>
                    </IconButton>
                )}
            </FormControl>
        </Box>
    )
}

SelectComponent.propTypes = {
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    startIcon: PropTypes.element,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            }),
        ])
    ).isRequired,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    error: PropTypes.bool,
    clearable: PropTypes.bool,
    clearAriaLabel: PropTypes.string,
    onClear: PropTypes.func,
    helperText: PropTypes.string,
    shouldShrinkLabel: PropTypes.bool,
}

export default SelectComponent
